import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, FileWarning } from 'lucide-react'
import { getBundles, getDraftManifest } from '@/api/bundles'
import { getDraftFile, putDraftFile } from '@/api/draftFiles'
import { getDraftPrompt } from '@/api/prompts'
import { Banner } from '@/components/Banner'
import { ConfirmModal } from '@/components/ConfirmModal'
import { EmptyState } from '@/components/EmptyState'
import { errorMessage } from '@/lib/apiErrors'
import { refreshAfterWrite } from '@/lib/refreshAfterWrite'
import type { ExtractorsFile, GlobalDefaultsFile, ProductType, ProductTypesFile } from '@/types/bundle'
import { ProductTypesTable, type ProductTypeRow } from './components/ProductTypesTable'
import { NewOrDuplicateProductTypeModal } from './components/NewOrDuplicateProductTypeModal'

function SkeletonBlock() {
  return <div className="h-48 animate-pulse rounded-control bg-surface-raised" />
}

export function ProductTypesListScreen() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const productTypesQuery = useQuery({
    queryKey: ['draft-file', 'product_types'],
    queryFn: () => getDraftFile<ProductTypesFile>('product_types'),
  })
  // "Last edited" has no backend per-product field (PRD 02's own Open
  // Question 1) - resolved as: the draft manifest's file-level timestamp for
  // product_types when a draft edit exists, else the live bundle's
  // published_at.
  const draftManifestQuery = useQuery({ queryKey: ['draft-manifest'], queryFn: getDraftManifest })
  const bundlesQuery = useQuery({ queryKey: ['bundles'], queryFn: getBundles })
  // For the New/Duplicate modal's default limits (buildEmptyProductType) -
  // same query key + fetcher the detail screen already uses for hard caps.
  const globalDefaultsQuery = useQuery({
    queryKey: ['draft-file', 'global_defaults'],
    queryFn: () => getDraftFile<GlobalDefaultsFile>('global_defaults'),
  })
  // Same reasoning - buildEmptyProductType also seeds a default extractor.
  const extractorsQuery = useQuery({
    queryKey: ['draft-file', 'extractors'],
    queryFn: () => getDraftFile<ExtractorsFile>('extractors'),
  })

  const [duplicateSource, setDuplicateSource] = useState<{ id: string; product: ProductType } | undefined>(
    undefined,
  )
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ProductTypeRow | null>(null)

  // Deleting from this list screen is immediate (confirm -> PUT), not staged
  // behind a Save button - unlike the detail screen, this screen has no
  // other local draft/edit state to stage a removal alongside.
  const deleteMutation = useMutation({
    mutationFn: (payload: ProductTypesFile) => putDraftFile<ProductTypesFile>('product_types', payload),
    onSuccess: () => {
      refreshAfterWrite(queryClient, { kind: 'draft-file', fileKey: 'product_types' })
    },
  })

  if (
    productTypesQuery.isPending ||
    draftManifestQuery.isPending ||
    bundlesQuery.isPending ||
    globalDefaultsQuery.isPending ||
    extractorsQuery.isPending
  ) {
    return <SkeletonBlock />
  }

  if (productTypesQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load product types">
        {errorMessage(productTypesQuery.error)}
      </Banner>
    )
  }

  if (draftManifestQuery.isError || bundlesQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load draft status">
        {errorMessage(draftManifestQuery.error) ?? errorMessage(bundlesQuery.error)}
      </Banner>
    )
  }

  if (globalDefaultsQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load platform defaults">
        {errorMessage(globalDefaultsQuery.error)}
      </Banner>
    )
  }

  if (extractorsQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load extractors">
        {errorMessage(extractorsQuery.error)}
      </Banner>
    )
  }

  const productTypesFile = productTypesQuery.data
  const productTypes = productTypesFile.product_types
  const productIds = Object.keys(productTypes)

  const draftEntry = draftManifestQuery.data.find((entry) => entry.fileKey === 'product_types')
  const liveBundle = bundlesQuery.data.find((bundle) => bundle.isLive)
  const lastEdited = draftEntry?.exists ? draftEntry.lastModified : liveBundle?.publishedAt

  const rows: ProductTypeRow[] = productIds.map((id) => ({
    id,
    product: productTypes[id],
    lastEdited,
  }))

  function openNewModal() {
    setDuplicateSource(undefined)
    setModalOpen(true)
  }

  function openDuplicateModal(row: ProductTypeRow) {
    setDuplicateSource({ id: row.id, product: row.product })
    setModalOpen(true)
  }

  // Duplicate's source Baseline Prompt content (if any) is fetched here,
  // before navigating, and seeded into the new (unsaved) form via router
  // state - it's written to a freshly-derived file only when the operator
  // actually saves there (Design PRD 10's Duplicate bullet; "nothing
  // persists until Save" applies to the prompt file exactly like every other
  // field). Best-effort: a fetch failure just means the new form starts
  // empty rather than blocking Duplicate entirely.
  async function handleConfirm(newId: string, initialEntry: ProductType) {
    const sourceFilename = duplicateSource?.product.extraction?.prompt_template_file
    let initialBaselinePromptContent: string | undefined
    if (sourceFilename) {
      try {
        const prompt = await getDraftPrompt(sourceFilename)
        initialBaselinePromptContent = prompt.content
      } catch {
        initialBaselinePromptContent = undefined
      }
    }
    navigate(`/product-types/${newId}`, { state: { initialEntry, initialBaselinePromptContent } })
  }

  function handleConfirmDelete() {
    if (!deleteTarget) return
    const nextProductTypes = { ...productTypes }
    delete nextProductTypes[deleteTarget.id]
    deleteMutation.mutate(
      { ...productTypesFile, product_types: nextProductTypes },
      { onSuccess: () => setDeleteTarget(null) },
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-title font-semibold text-text-primary">Products</h1>
        <button
          type="button"
          onClick={openNewModal}
          className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white"
        >
          + New Product Type
        </button>
      </div>

      {deleteMutation.isError && (
        <Banner variant="danger" icon={AlertCircle} title="Delete failed">
          {errorMessage(deleteMutation.error)}
        </Banner>
      )}

      {productIds.length === 0 ? (
        <EmptyState
          icon={FileWarning}
          headline="No product types defined yet"
          description="Once a product type is added to the bundle, it will appear here for editing."
          action={{ label: '+ New Product Type', onClick: openNewModal }}
        />
      ) : (
        <ProductTypesTable rows={rows} onDuplicate={openDuplicateModal} onDelete={setDeleteTarget} />
      )}

      <NewOrDuplicateProductTypeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        existingIds={productIds}
        hardCaps={globalDefaultsQuery.data.hard_caps}
        extractors={extractorsQuery.data.extractors}
        source={duplicateSource}
        onConfirm={handleConfirm}
      />

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete product type"
        description={`"${deleteTarget?.product.description ?? deleteTarget?.id}" will be removed immediately - there is no separate Save step on this screen. Make sure it's not still required by any client override or referenced elsewhere first.`}
        confirmLabel={deleteMutation.isPending ? 'Deleting…' : 'Delete'}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
